import { ChordLib, ScaleLib } from '@tunasong/music-lib'
import type { Chord } from '@tunasong/music-lib'
import type { MelodyNote, NoteName, Scale } from '@tunasong/schemas'
import type { FC } from 'react'
import styles from './melody-staff.module.css'

export interface MelodyStaffProps {
  scale: Scale
  chords: Chord[]
  notes: MelodyNote[]
  onPlaceNote: (note: MelodyNote) => void
  onRemoveNote: (note: MelodyNote) => void
}

const BEATS_PER_CHORD = 4
const DEFAULT_OCTAVE = 4

/**
 * Pitch × Beat grid input surface for the Melody Writer.
 *
 * - Columns: one per chord in the section (4 beats each in v1, matching
 *   Seymour's F.2 progression layout).
 * - Rows: in-scale pitches descending from the octave's 7th degree to its
 *   root. Major scales yield 7 rows; modes inherit their own intervals.
 * - Slots: each (chord, pitch) cell. Click empty slot → place note. Click
 *   existing note → remove note. Multiple notes per (chord, pitch) cell are
 *   not supported in v1 (the grid is monophonic per slot).
 *
 * Spec §6 (UI walkthrough) details the visual treatment. Pixel design is
 * Joni's BUILD hand-off; this file ships the data contract.
 */
export const MelodyStaff: FC<MelodyStaffProps> = props => {
  const { scale, chords, notes, onPlaceNote, onRemoveNote } = props
  // ScaleLib.getNotes returns one note per interval including the VIII octave
  // (same pitch class as the root). Drop the octave so the grid has one row
  // per unique scale-degree pitch class.
  const scaleNotes = ScaleLib.getNotes(scale).slice(0, -1)
  // Render pitches top-to-bottom = high-to-low.
  const pitchesTopToBottom = [...scaleNotes].reverse()

  const placedAt = (chordIdx: number, pitch: NoteName) =>
    notes.find(n => Math.floor(n.position / BEATS_PER_CHORD) === chordIdx && n.pitch === pitch)

  return (
    <div className={styles.staff} role="grid" aria-label="Melody staff">
      <div className={styles.chordRow}>
        {chords.map((chord, idx) => (
          <div key={idx} className={styles.chordHeader} data-testid={`chord-column-${idx}`}>
            {ChordLib.getName(chord)}
          </div>
        ))}
      </div>
      <div className={styles.grid}>
        {pitchesTopToBottom.map(pitch => (
          <div key={pitch} className={styles.pitchRow} data-testid={`pitch-row-${pitch}`} role="row">
            {chords.map((_, chordIdx) => {
              const note = placedAt(chordIdx, pitch)
              return (
                <button
                  key={chordIdx}
                  type="button"
                  className={note ? styles.slotFilled : styles.slot}
                  data-testid={note ? `placed-note-${chordIdx}-${pitch}` : `staff-slot-${chordIdx}-${pitch}`}
                  aria-label={
                    note
                      ? `Remove ${pitch}${DEFAULT_OCTAVE} on chord ${chordIdx + 1}`
                      : `Place ${pitch}${DEFAULT_OCTAVE} on chord ${chordIdx + 1}`
                  }
                  role="gridcell"
                  onClick={() => {
                    if (note) {
                      onRemoveNote(note)
                    } else {
                      onPlaceNote({
                        pitch,
                        octave: DEFAULT_OCTAVE,
                        duration: '4n',
                        position: chordIdx * BEATS_PER_CHORD,
                      })
                    }
                  }}
                />
              )
            })}
          </div>
        ))}
      </div>
    </div>
  )
}
